import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import Button from '../Button/Button';
import AlertModal from '../AlertModal/AlertModal';
import './TwoFactor.scss';

/**
 * Fluxo de ativação do 2FA (TOTP) — usado tanto no login obrigatório de um admin sem 2FA
 * ainda configurado (`token` = setup_token de /auth/token) como na ativação opcional a
 * partir do Perfil (`token` = access_token da sessão atual). O backend aceita os dois
 * (app/api/v1/auth_2fa.py::_resolve_setup_user).
 *
 * Passos: 1) pede o secret+QR ao backend (POST /2fa/setup) 2) utilizador digitaliza com a
 * app autenticadora 3) confirma com um código de 6 dígitos (POST /2fa/setup/confirm) 4)
 * mostra os códigos de recuperação UMA única vez.
 */
export default function TwoFactorSetup({ token, axiosAuth, onDone, onCancel }) {
  const [secret, setSecret] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosAuth.post('/auth/2fa/setup', { token });
        if (cancelled) return;
        setSecret(data.secret);
        const dataUrl = await QRCode.toDataURL(data.otpauth_url);
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.detail ||
              'Não foi possível iniciar a configuração do 2FA.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleConfirm(e) {
    e.preventDefault();
    if (confirming || code.length < 6) return;
    setConfirming(true);
    setError(null);
    try {
      const { data } = await axiosAuth.post('/auth/2fa/setup/confirm', {
        token,
        code,
      });
      setRecoveryCodes(data.recovery_codes);
      setAccessToken(data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Código inválido.');
      setCode('');
    } finally {
      setConfirming(false);
    }
  }

  // passo 4: códigos de recuperação (só aparecem uma vez — nunca mais são visíveis)
  if (recoveryCodes) {
    return (
      <div className="two-factor-flow">
        <h3>Guarda os teus códigos de recuperação</h3>
        <p className="two-factor-hint">
          Usa um destes códigos, uma única vez cada, se perderes o acesso à app
          autenticadora. Não vão voltar a ser mostrados.
        </p>
        <div className="recovery-codes-grid">
          {recoveryCodes.map((c) => (
            <code key={c}>{c}</code>
          ))}
        </div>
        <div className="two-factor-actions">
          <Button onClick={() => onDone(accessToken)}>
            Já guardei os códigos — continuar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="two-factor-flow">
      {error && (
        <AlertModal
          variant="danger"
          title="Aviso"
          message={error}
          onClose={() => setError(null)}
        />
      )}
      <h3>Configura a verificação em duas etapas</h3>
      <p className="two-factor-hint">
        A tua função exige 2FA para proteger o painel. Digitaliza o código com a
        tua app autenticadora (Google Authenticator, Authy, 1Password...).
      </p>
      {loading ? (
        <p>A gerar código...</p>
      ) : (
        <>
          {qrDataUrl && (
            <img className="two-factor-qr" src={qrDataUrl} alt="QR code do 2FA" />
          )}
          {secret && (
            <p className="two-factor-secret">
              Não consegues ler o QR? Insere manualmente: <code>{secret}</code>
            </p>
          )}
          <form onSubmit={handleConfirm} className="two-factor-code-form">
            <input
              className="input"
              type="text"
              inputMode="numeric"
              placeholder="Código de 6 dígitos"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              required
            />
            <div className="two-factor-actions">
              <Button type="submit" disabled={confirming || code.length < 6}>
                Confirmar
              </Button>
              {onCancel && (
                <Button type="button" variant="secondary" onClick={onCancel}>
                  Cancelar
                </Button>
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
}
